// WHICH FORGE a stored native id belongs to (#3984, plan 101 slice 06).
//
// DECISION #3984 (option a, expand then contract): every table that holds its OWN
// forge id carries `forge` + `instance` beside the existing `github_*` column, and
// its uniqueness is `(forge, instance, native id)`. The `github_*` names stay and
// mean "the native id on this row's forge"; no value is rewritten.
//
// HERE, not beside the columns, because the vocabulary now has readers on both
// sides of the db boundary. It arrived in `packages/db/src/schema/forge.ts` beside
// `forgeColumns()`, which was right while only SQL predicates named it. Slice 07b
// carries `forge`/`instance` onto the {@link Repository} entity so a PURE
// authorisation fence can read them (`pickEnabledRepo`, `apps/api`), and
// `@developerz/domain` imports nothing internal — so a vocabulary reachable from
// domain is the only shape in which the SQL writers and the entity readers share
// ONE definition instead of retyping the literals at each end. The post-mortem for
// retyping them is in this package already: `AI_FIRST_SOURCES`
// (`entities/repository.ts`) says why a second copy of a value set does not fail
// loudly, it just stops agreeing, and the deleted `PolicySchema` duplicate at the
// foot of that file is the same defect with a cost attached.
//
// `packages/db/src/schema/forge.ts` keeps the COLUMN and PREDICATE constructors
// (`forgeColumns`, `forgeCheck`, `githubForgeRow`, `forgeKey`) and re-exports these
// four names, so its existing importers are unchanged.

/**
 * The forges a stored row may name. Closed by a CHECK on every carrying table
 * (`<table>_forge_ck`, built from this array by `forgeCheck`) — text + CHECK, the
 * D3 no-pgEnum stance. Widening it is a migration that rewrites those CHECKs.
 */
export const FORGES = ['github', 'gitlab', 'gitee'] as const;
export type Forge = (typeof FORGES)[number];

/** What every pre-#3984 row is, and what a GitHub writer still states by default. */
export const GITHUB_FORGE = 'github' satisfies Forge;

/**
 * The second forge, named rather than spelled at each writer (#7531).
 *
 * It exists because a stored `'gitlab'` is now written, not only read: the row writer,
 * its unit tests and its live-DB proof would otherwise each carry the literal, and a
 * typo in one of them is a row no fence recognises as GitLab's and no GitHub lane
 * refuses either — `repositories_forge_ck` would catch it, but only at the INSERT.
 */
export const GITLAB_FORGE = 'gitlab' satisfies Forge;

/**
 * The GitHub.com INSTANCE. Instances are part of every uniqueness key because
 * GitLab ids are per INSTANCE (two self-hosted GitLabs reuse them), so
 * `(forge, native id)` would be under-keyed by one column — the expensive kind to
 * discover with data in it.
 */
export const GITHUB_INSTANCE = 'github.com';

/**
 * The pair every forge-qualified row carries, named so the predicate below takes a
 * `Repository` (`entities/repository.ts`) or any narrower row shape holding both
 * columns, without this module depending on the entity that depends on it.
 */
export type ForgeQualified = { readonly forge: Forge; readonly instance: string };

/**
 * Is this row GitHub.com's? The ENTITY-SIDE twin of `githubForgeRow` (the SQL
 * predicate in `@developerz/db`'s `schema/forge.ts`), and the one spelling every
 * lane above the db boundary uses.
 *
 * WHY A FUNCTION AND NOT TWO COMPARISONS AT EACH SITE. The fence is an
 * authorisation decision — it is what lets a GitHub-only lane refuse a row it must
 * not act on — and slice 07b counts a dozen sites that need it. Written out, the
 * instance half is the one people would drop: `forge === 'github'` reads complete
 * and is under-keyed by exactly the column that makes two self-hosted GitLabs
 * distinct. One function cannot be half-applied.
 *
 * It is a REFUSAL, never a filter that silently narrows a tenant's own inventory:
 * `listRepositoriesByAccount` and its paged twin stay forge-neutral and keep
 * returning every row, because at the second forge's write path the account's
 * GitLab repositories are theirs to see. Which reads earn the exemption, and the
 * `forge-neutral:` marker that states it, are in `schema/forge.ts`'s docblock.
 */
export function isGithubRow(row: ForgeQualified): boolean {
  return row.forge === GITHUB_FORGE && row.instance === GITHUB_INSTANCE;
}
